import { FC, useRef } from "react";
import { Button, Card, CardHeader, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Image, InfoLabel, Input, Label, Radio, RadioGroup, Select, Text, Toast, ToastBody, Toaster, ToastTitle, useId, useToastController } from "@fluentui/react-components";
//import { useSearchParams } from "react-router-dom";
import { IConference } from "./conferenceTypes";
import { useGeneralStyles } from "../styles";
import { useQuery } from "@tanstack/react-query";
import { nodeServer } from "../constants";
import { Controller, useForm } from "react-hook-form";
import { ISponsorshipInfo } from "../../shared/conferenceModels";
import FilePicker from "../FilePicker";
import { getRandomElementName } from "../../shared/chemical";

interface ISponsorRegistration {}

const SponsorRegistration: FC<ISponsorRegistration> = () => {
    //const [searchParams] = useSearchParams();

    const toasterId = useId("toaster");
    const { dispatchToast } = useToastController(toasterId);
    const notify = (type: "error" | "success", message: string) => {
        if (type === "error") {
            dispatchToast(
                <Toast>
                    <ToastTitle>Error</ToastTitle>
                    <ToastBody>{message}</ToastBody>
                </Toast>,
                { intent: "error" },
            );
        } else {
            dispatchToast(
                <Toast>
                    <ToastTitle>Success</ToastTitle>
                    <ToastBody>{message}</ToastBody>
                </Toast>,
                { intent: "success" },
            );
        }
    };

    const { data: conference } = useQuery({
        queryKey: ["conference"],
        refetchOnWindowFocus: false,
        enabled: true,
        queryFn: async (signal) => {
            const url = nodeServer + `conference/latest`;
            const res = await fetch(url, { signal: signal.signal });
            if (!res.ok) {
                throw new Error("Network response was not ok");
            }
            const conference: IConference = await res.json();
            return conference;
        },
    });

    

    const uploadImageAsync = async (file: File): Promise<{ error?: string; url?: string } | null> => {
        //if (accessTokenInfo$.value !== null && accessTokenInfo$.value.accessToken !== null) {
        let headers = new Headers();

        var form_data = new FormData();
        form_data.append("file", file);
        form_data.append("location", "sponsor");
        const elementName = getValues("elementName");
        const atomicNumber = getValues("atomicNumber");
        form_data.append("elementName", elementName);
        form_data.append("atomicNumber", atomicNumber.toString());
        const response = await fetch(nodeServer + "uploadSponsorImage", { method: "POST", body: form_data, headers: headers });
        const json = await response.json();
        return json;
        // } else {
        //     return "Not authenticated!";
        // }
    };

    const {
        handleSubmit,
        control,
        getValues,
        //formState: { errors },
    } = useForm<ISponsorshipInfo>({
        defaultValues: {
            tier: 0,
            elementName: getRandomElementName(),
            atomicNumber: 0,
            conferenceId: 0,
        },
    });

    const onSubmit = async (values: ISponsorshipInfo) => {
        const submission: Partial<ISponsorshipInfo> = {};

        submission.conferenceId = conference!.id;
        submission.title = values.title;
        submission.tier = values.tier;
        submission.imageUrl = values.imageUrl;
        submission.linkUrl = values.linkUrl;
        submission.contactName = values.contactName;
        submission.contactEmail = values.contactEmail;
        submission.contactAddress = values.contactAddress;
        submission.contactPhone = values.contactPhone;
        submission.payby = values.payby;
        submission.paid = false;
        switch (+values.tier) {
            case 0:
                submission.cost = 400;
                break;
            case 1:
                submission.cost = 800;
                break;
            case 2:
                submission.cost = 1200;
                break;
            default:
                submission.cost = 400;
        }

        let headers = new Headers();
        headers.append("Content-Type", "application/json");
        const response = await fetch(nodeServer + "conference/sponsor/register", { method: "post", body: JSON.stringify(submission), headers: headers });
        console.log(response);
        const result = await response.json();
        if (!result.error) {
            window.open(`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=memberfee@2yc3.org&item_name=${encodeURIComponent(conference!.number)}_${encodeURIComponent(submission.title)}_${encodeURIComponent(submission.contactName)}&amount=${submission.cost}`, "_blank");
            console.log(result);
            notify("success", "Registration successful! You have been sent a confirmation email. 2YC3 will contact you soon with further details.");
        }
    };

    const getImageForTier = () => {
        const imageUrl = getValues("imageUrl");
        if (imageUrl && imageUrl !== "") {
            console.log(getValues("imageUrl"));
            console.log(getValues("tier"));
            switch (+getValues("tier")) {
                case 0:
                    return (
                        <>
                            <Label>Preview</Label>
                            <Image width={100} height={100} style={{ width: 100, height: 100 }} fit="contain" src={getValues("imageUrl")} />
                        </>
                    );
                case 1:
                    return (
                        <>
                            <Label>Preview</Label>
                            <Image width={200} height={200} style={{ width: 200, height: 200 }} fit="contain" src={getValues("imageUrl")} />
                        </>
                    );
                case 2:
                    return (
                        <>
                            <Label>Preview</Label>
                            <Image width={300} height={300} style={{ width: 300, height: 300 }} fit="contain" src={getValues("imageUrl")} />
                        </>
                    );
                default:
                    return (
                        <>
                            <Label>Preview</Label>
                            <Image width={300} height={300} style={{ width: 300, height: 300 }} fit="contain" src={getValues("imageUrl")} />
                        </>
                    );
            }
        } else {
            return <></>;
        }
    };
   

    const generalStyles = useGeneralStyles();

    const sponsorId = useId("div");
    const titleId = useId("input");
    const imagePickerId = useId("input");
    const atomicNumberId = useId("input");
    const imageUrlId = useId("input");
    const imgSrcRef = useRef<HTMLInputElement>(null);
    const linkUrlId = useId("input");
    const tierId = useId("input");
    const contactNameId = useId("input");
    const contactEmailId = useId("input");
    const contactAddressId = useId("input");
    const contactPhoneId = useId("input");
    const paybyId = useId("input");

    return (
        <div >
            <div style={{ display: "flex", flexDirection: "column", flexWrap: "wrap", gap: "5px" }}>
                <h2>
                    {conference && "2YC3"} {conference?.number} {conference && "Conference at Montgomery College: "} Sponsorship Information
                </h2>

                <Card>
                    <Text className={generalStyles.body1}>
                        Montgomery College is hosting the 226th Two-Year College Chemistry Consortium, 2YC3, Conference May 29-30, 2026 at the Takoma Park-Silver Spring campus. The conference will bring together ~75-100 chemical educators primarily from two-year colleges from regional and national
                        institutions. Attendees include faculty, department leaders, lab managers and instructional partners committed to advancing chemical education. Sponsorship offers visibility and engagement with educators and the opportunity to build relationships with individuals that
                        influence curriculum, purchasing decisions and student success initiatives.{" "}
                    </Text>
                    <Text className={generalStyles.body1}>
                        We will have a vendor exhibition on Friday, May 29 from 8:00 am - 4:00 pm and Saturday, May 30 from 8:00 am - noon. The exhibition will take place in the Catherine and Isiah Leggett Math and Science Building (LB). There will be volunteers available to assist both days and
                        space available for secure overnight storage if needed.{" "}
                    </Text>
                    <Text className={generalStyles.body1}>
                        We will be collecting a door prize from each sponsor and distributing engagement cards to each conference attendee at registration. Attendees will need to visit every vendor to enter the drawing for door prizes. The door prize drawing will take place after the lunch on
                        Saturday.{" "}
                    </Text>
                </Card>

                <Card>
                    <CardHeader header={<Text className={generalStyles.cardTitle}>Tentative schedule of events in the exhibition/vendor space: </Text>} />
                    <Text className={generalStyles.secondary}>Day 1 – Friday, May 29, 2026</Text>
                    <ul>
                        <li>8:00 – 9:00 am Registration, Coffee and breakfast </li>
                        <li> 11:30am - 1:30 pm Vendor Expo visit time / Boxed lunch</li>
                        <li> 3:15 - 3:45 pm Afternoon snack break / Vender Expo visit time</li>
                    </ul>
                    <Text className={generalStyles.secondary}>Day 2 – Saturday, May 30, 2026</Text>
                    <ul>
                        <li> 8:00 – 9:00 am Coffee and breakfast </li>
                        <li> 10:15 – 10:45 am Coffee break / Vendor Expo visit time </li>
                    </ul>
                </Card>

                <Card style={{}}>
                    <CardHeader header={<Text className={generalStyles.cardTitle}>Tiers</Text>} />
                    <Text className={generalStyles.secondary}>Bronze Visibility Sponsorship: ($400 + item for door prize)</Text>
                    <ul style={{ margin: 5 }}>
                        <li>Conference registration for 1 representative (lunch included both days) </li>
                        <li>6-foot table in vendor expo area</li>
                        <li>Designated stop on attendee engagement card</li>
                        <li>Name/logo included in list of sponsors</li>
                    </ul>

                    <Text className={generalStyles.secondary}>Silver Engagement Sponsorship ($800 + item for door prize)</Text>
                    <ul style={{ margin: 5 }}>
                        <li>Conference registration for up to 2 representatives (lunch included both days) </li>
                        <li>6-foot table in vendor expo area</li>
                        <li>Designated stop on attendee engagement card</li>
                        <li>Prominent Name/logo placement on list of sponsors</li>
                        <li>Vendor presentation session (One 20-minute presentation slot in program schedule)</li>
                    </ul>

                    <Text className={generalStyles.secondary}>Gold Partnership Sponsorship ($1200 + item for door prize)</Text>
                    <ul style={{ margin: 5 }}>
                        <li>Conference registration for up to 3 representatives (lunch included both days) </li>
                        <li>6-foot table in vendor expo area in prime location (high traffic placement)</li>
                        <li>Designated stop on attendee engagement card</li>
                        <li>Premium Name/logo placement on list of sponsors</li>
                        <li>Sponsored coffee break or refreshment break (with signage)</li>
                        <li>Vendor presentation session (One 20-minute presentation slot or one 60–90-minute workshop) </li>
                        <li>Special recognition at Welcome event and Keynote luncheon</li>
                    </ul>
                </Card>

                <Card>
                    <CardHeader header={<Text className={generalStyles.cardTitle}>Register as a Sponsor</Text>} />
                    <Text className={generalStyles.body1} style={{ marginBottom: "20px" }}>
                        Thank you for your interest in sponsoring our conference! Please fill out the form below to register as a sponsor. After submitting the form, you will be forwarded to a Paypal page to complete your transaction.
                    </Text>
                    <Text>
                        To discuss sponsorship options, payment and other details, please contact our planning team at <a href="mailto:2YC3@montgomerycollege.edu">2YC3@montgomerycollege.edu</a>
                    </Text>

                    <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column" }} method="POST">
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }} id={sponsorId}>
                            <Label required htmlFor={tierId}>
                                Sponsorship Tier
                            </Label>
                            <Controller
                                name="tier"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field }) => (
                                    <Select id={tierId} {...field} required>
                                        {/* <option value="">Select a tier</option> */}
                                        <option key="0" value="0" defaultChecked>
                                            Bronze Visibility $400+
                                        </option>
                                        <option key="1" value="1">
                                            Silver Engagement $800+
                                        </option>
                                        <option key="2" value="2">
                                            Gold Partnership $1,200+
                                        </option>
                                    </Select>
                                )}
                            />

                            <div>
                                <Text className={generalStyles.body2} style={{ marginBottom: "5px" }}>
                                    {}
                                </Text>
                            </div>

                            <InfoLabel htmlFor={titleId} required info={<>What viewers will see in text format.</>}>
                                Sponsor Title
                            </InfoLabel>
                            <Controller
                                name="title"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field }) => <Input id={titleId} {...field} required />}
                            />

                            <Card appearance="filled-alternative" style={{ marginTop: "10px", marginBottom: "10px" }}>
                                <Label weight="semibold">Anti-spam: Answer the question to upload your image</Label>
                                <Label>What is the atomic number of {control._defaultValues.elementName}?</Label>
                                <Controller
                                    name="atomicNumber"
                                    control={control}
                                    rules={{
                                        required: false,
                                    }}
                                    render={({ field }) => <Input id={atomicNumberId} {...field} value={field.value.toString()} required />}
                                />
                                <InfoLabel htmlFor={imageUrlId} required info={<>What viewers will see on the website. You can upload it to our website or host it elsewhere and just provide the link.</>}>
                                    Image URL
                                </InfoLabel>
                                <Controller
                                    name="imageUrl"
                                    control={control}
                                    rules={{
                                        required: true,
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <FilePicker
                                                id={imagePickerId}
                                                onChange={async (file) => {
                                                    //immediate upload
                                                    const result = await uploadImageAsync(file);
                                                    console.log("image uploaded");
                                                    console.log(result);

                                                    if (result && result.url && imgSrcRef.current) {
                                                        notify("success", "Image uploaded successfully!");
                                                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")!.set;
                                                        nativeInputValueSetter!.call(imgSrcRef.current, "https://2yc3.org/uploads/sponsor/" + file.name);
                                                        // previous 2 lines required because we're using React.
                                                        var event = new Event("change", { bubbles: true });
                                                        imgSrcRef.current.dispatchEvent(event);
                                                    } else {
                                                        notify("error", result && result.error ? result.error : "There was an error uploading your image. Please try again or contact the webmaster.");
                                                    }
                                                }}
                                            >
                                                Choose an image to upload...
                                            </FilePicker>
                                            <Input id={imageUrlId} {...field} ref={imgSrcRef} required />
                                            {getImageForTier()}
                                        </>
                                    )}
                                />
                            </Card>

                            <InfoLabel htmlFor={linkUrlId} required info={<>The URL where the user will be taken when clicking the sponsor image or title.</>}>
                                Link URL
                            </InfoLabel>
                            <Controller
                                name="linkUrl"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field }) => (
                                    <>
                                        <Input id={linkUrlId} {...field} required />
                                    </>
                                )}
                            />

                            <Label required htmlFor={contactNameId}>
                                Contact Name
                            </Label>
                            <Controller
                                name="contactName"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field }) => <Input id={contactNameId} {...field} required />}
                            />

                            <Label required htmlFor={contactEmailId}>
                                Contact Email
                            </Label>
                            <Controller
                                name="contactEmail"
                                control={control}
                                rules={{
                                    required: true,
                                    pattern: {
                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                        message: "Please enter a valid email address",
                                    },
                                }}
                                render={({ field }) => <Input id={contactEmailId} {...field} type="email" required />}
                            />

                            <Label htmlFor={contactAddressId}>Contact Address</Label>
                            <Controller
                                name="contactAddress"
                                control={control}
                                rules={{
                                    required: false,
                                }}
                                render={({ field }) => <Input id={contactAddressId} {...field} />}
                            />

                            <Label htmlFor={contactPhoneId}>Contact Phone</Label>
                            <Controller
                                name="contactPhone"
                                control={control}
                                rules={{
                                    required: false,
                                }}
                                render={({ field }) => <Input id={contactPhoneId} {...field} />}
                            />

                            <Label htmlFor={paybyId} required>
                                Pay By
                            </Label>
                            <Controller
                                name="payby"
                                control={control}
                                rules={{
                                    required: false,
                                }}
                                render={({ field }) => (
                                    <RadioGroup id={paybyId} {...field} style={{ display: "flex", flexDirection: "row", gap: 20 }} required>
                                        <Radio value="paypal" label="Paypal" defaultChecked />
                                        <Radio value="check" label="Check" />
                                    </RadioGroup>
                                )}
                            />
                        </div>

                        <Button style={{ marginTop: "20px", marginBottom: "20px" }} type="submit" appearance="primary">
                            Submit
                        </Button>

                        <Dialog>
                            <DialogTrigger disableButtonEnhancement></DialogTrigger>
                            <DialogSurface>
                                <DialogBody>
                                    <DialogTitle>Thank you!</DialogTitle>
                                    <DialogContent>
                                        <Text className={generalStyles.body2}>You have been forwarded (via a new tab) to a Paypal page to complete your transaction. 2YC3 will contact you soon with further details.</Text>
                                    </DialogContent>
                                    <DialogActions>
                                        <DialogTrigger disableButtonEnhancement>
                                            <Button appearance="primary">Dismiss</Button>
                                        </DialogTrigger>
                                    </DialogActions>
                                </DialogBody>
                            </DialogSurface>
                        </Dialog>
                    </form>
                </Card>
            </div>
            <Toaster toasterId={toasterId} />
        </div>
    );
};

export default SponsorRegistration;
